import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  callOffProject as callOffProjectApi,
  completeProject as completeProjectApi,
  deleteApplication,
  getUserApplication,
  getProjectBillingDetails,
  getProjectById,
  offerAccepted as offerAcceptedApi,
  offerRejected as offerRejectedApi,
  pitchLost as pitchLostApi,
  startTendering as startTenderingApi,
  startBilling as startBillingApi,
  updateProjectBillingDetails,
  updateProjectCompletionNote as updateProjectCompletionNoteApi,
  updateProjectClosureWaiver as updateProjectClosureWaiverApi,
  updateProjectMemberBillingDetails,
  updateProjectData as updateProjectDataApi,
  updateBTAllocation,
  updateStaffingCommittee,
  saveTeamComposition as saveTeamCompositionApi,
  getProjectApplications,
  startSecondApplicationPhase as startSecondApplicationPhaseApi,
} from "../../api/projects";
import {
  CompleteProjectDto,
  MemberBTAllocationDto,
  ProjectBillingDocumentUpdateDto,
  ProjectMemberUpdateDto,
  ProjectTeamCompositionDto,
  ProjectUpdateDto,
  SecondApplicationPhaseDto,
  UpdatedProjectDataDto,
} from "../../types/projectTypes";
import { updateProject as updateProjectApi } from "../../api/projects";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { AxiosError } from "axios";
import { authReducerActionType } from "../../types/globalTypes";
import { useContext } from "react";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { MembersFieldDto } from "../../types/membersTypes";
import { useNavigate } from "react-router-dom";

const useProjectsDetails = (projectId: number) => {
  const queryClient = useQueryClient();
  const { auth, dispatchAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  const invalidateProjectStatusQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    queryClient.invalidateQueries({ queryKey: ["tenderedProjects"] });
  };

  // ############
  // GET QUERIES
  // ############

  const { data: projectDetailsData, isLoading: isProjectDetailsLoading } = useQuery({
    queryKey: ["projectDetails", projectId],
    queryFn: () => getProjectById(projectId),
    enabled: !!projectId,
  });

  const projectDetails = projectDetailsData?.data;

  const { data: applicationData, isLoading: isUserApplicationLoading } = useQuery({
    queryKey: ["userApplication", projectId],
    queryFn: () => getUserApplication(projectId),
    enabled: !!projectId,
  });

  // the application data for the user
  const userApplication = applicationData?.data;

  const { data: projectApplicationsData, isLoading: isProjectApplicationsLoading } = useQuery({
    queryKey: ["projectApplications", projectId],
    queryFn: () => getProjectApplications(projectId),
    enabled: !!projectId,
  });

  const projectApplications = projectApplicationsData?.data;

  const hasPermissionProjectBilling = doesPermissionsHaveSomeOf(auth?.permissions, [7, 11, 22]);
  const isProjectMember = projectDetails?.members?.some((member) => member.memberId === auth?.userID);
  const isProjectQM = projectDetails?.qms?.some((qm) => qm.memberId === auth?.userID);
  const isProjectCorrectForBilling =
    projectDetails?.status === "Durchführung" ||
    projectDetails?.status === "Abrechnung" ||
    projectDetails?.status === "Abgeschlossen";

  const { data: projectBillingDetailsData, isLoading: isProjectBillingDetailsLoading } = useQuery({
    queryKey: ["projectBillingDetails", projectId],
    queryFn: () => getProjectBillingDetails(projectId),
    // only fetch the project billing details if the user has the necessary permissions or is a member of the project and the project is in the correct status
    // otherwise the query would be executed for every project, which is will result in a lot of unnecessary requests
    enabled: (!!hasPermissionProjectBilling || !!isProjectMember || !!isProjectQM) && !!isProjectCorrectForBilling,
  });

  const projectBillingDetails = projectBillingDetailsData?.data;
  // ----------------------------------------------------------------------------------

  // ############
  // UPDATE QUERIES
  // ############

  // updateProject mutation
  const { mutateAsync: updateProjectMutate } = useMutation({
    mutationFn: updateProjectApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Änderungen erfolgreich gespeichert!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 401) {
        showErrorMessage("Keine Berechtigung!");
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      } else {
        showErrorMessage("Fehler beim Speichern der Änderungen!");
      }
    },
  });

  /**
   * Update a project
   * @param requestBody The project to update as ProjectUpdateDto
   */
  const updateProject = async (requestBody: ProjectUpdateDto) => {
    await updateProjectMutate(requestBody);
  };

  // callOffProject mutation
  const { mutateAsync: callOffProjectMutate } = useMutation({
    mutationFn: callOffProjectApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Status erfolgreich geändert!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 409) {
        showErrorMessage("Projekt befindet sich im falschen Status für diese Aktion!");
      } else {
        showErrorMessage("Fehler beim Ändern des Status!");
      }
    },
  });

  /**
   * Call off a project
   * Changes the status of the project to "NichtBesetzt" if the project is in the status "Bewerbung"
   * Changes the status of the project to "Abgebrochen" if the project is in the status "Durchführung"
   */
  const callOffProject = async () => {
    await callOffProjectMutate(projectId);
  };

  // startTendering mutation
  const { mutateAsync: startTenderingMutate } = useMutation({
    mutationFn: startTenderingApi,
    onSuccess: () => {
      invalidateProjectStatusQueries();
      showSuccessMessage("Status erfolgreich geändert!");
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message;
      if (message === `Project ${projectId} has no valid application end`) {
        showErrorMessage("Bitte zuerst eine gültige Bewerbungsfrist setzen.");
      } else if (error.response?.status === 409) {
        showErrorMessage("Projekt befindet sich im falschen Status für diese Aktion!");
      } else {
        showErrorMessage("Fehler beim Ändern des Status!");
      }
    },
  });

  /**
   * Start the tendering for a lead project
   * Changes the status of the project to "Bewerbung"
   */
  const startTendering = async () => {
    try {
      await startTenderingMutate(projectId);
    } catch {
      // Error toast is handled in the mutation.
    }
  };

  // offerAccepted mutation
  const { mutateAsync: offerAcceptedMutate } = useMutation({
    mutationFn: offerAcceptedApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Status erfolgreich geändert!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 409) {
        showErrorMessage("Projekt befindet sich im falschen Status für diese Aktion!");
      } else {
        showErrorMessage("Fehler beim Ändern des Status!");
      }
    },
  });

  /**
   * Mark the offer as accepted
   * Changes the status of the project to "Durchführung"
   */
  const offerAccepted = async () => {
    await offerAcceptedMutate(projectId);
  };

  // offerRejected mutation
  const { mutateAsync: offerRejectedMutate } = useMutation({
    mutationFn: offerRejectedApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Status erfolgreich geändert!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 409) {
        showErrorMessage("Projekt befindet sich im falschen Status für diese Aktion!");
      } else {
        showErrorMessage("Fehler beim Ändern des Status!");
      }
    },
  });

  /**
   * Mark the offer as rejected
   * Changes the status of the project to "AngebotAbgelehnt"
   */
  const offerRejected = async () => {
    await offerRejectedMutate(projectId);
  };

  // pitchLost mutation
  const { mutateAsync: pitchLostMutate } = useMutation({
    mutationFn: pitchLostApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Status erfolgreich geändert!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 409) {
        showErrorMessage("Projekt befindet sich im falschen Status für diese Aktion!");
      } else {
        showErrorMessage("Fehler beim Ändern des Status!");
      }
    },
  });

  /**
   * Mark the pitch as lost
   * Changes the status of the project to "PitchVerloren"
   */
  const pitchLost = async () => {
    await pitchLostMutate(projectId);
  };

  // updateProjectData mutation
  const { mutateAsync: updateProjectDataMutate } = useMutation({
    mutationFn: (updatedProjectData: UpdatedProjectDataDto) => updateProjectDataApi(projectId, updatedProjectData),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["projectBillingDetails", projectId] }),
      ]);
      showSuccessMessage("Änderungen erfolgreich gespeichert!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 422) {
        showErrorMessage("Ungültige Änderungen!");
      } else if (error.response?.status === 401) {
        showErrorMessage("Keine Berechtigung!");
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      } else {
        showErrorMessage("Fehler beim Speichern der Änderungen!");
      }
    },
  });

  // startBilling mutation
  const { mutateAsync: startBillingMutate } = useMutation({
    mutationFn: startBillingApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectBillingDetails", projectId] });
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 401) {
        showErrorMessage("Keine Berechtigung!");
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      } else {
        showErrorMessage("Fehler beim Starten der Abrechnung!");
      }
    },
  });

  /**
   * Start the billing for the project
   */
  const startBilling = async () => {
    await startBillingMutate(projectId);
  };

  // completeProject mutation closes the project with a mandatory Abschlussnotiz.
  const { mutateAsync: completeProjectMutate } = useMutation({
    mutationFn: (requestBody: CompleteProjectDto) => completeProjectApi(projectId, requestBody),
    onSuccess: () => {
      invalidateProjectStatusQueries();
      queryClient.invalidateQueries({ queryKey: ["projectBillingDetails", projectId] });
      showSuccessMessage("Projekt erfolgreich abgeschlossen!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 401) {
        showErrorMessage("Keine Berechtigung!");
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      } else if (error.response?.status === 422) {
        showErrorMessage("Projekt kann nicht abgeschlossen werden!");
      } else {
        showErrorMessage("Fehler beim Abschließen des Projekts!");
      }
    },
  });

  const completeProject = async (note: string, followUpActivityId: number | null, waiverReason: string | null) => {
    await completeProjectMutate({
      projectCompletionNote: note,
      followUpActivityId,
      projectClosureWaiverReason: waiverReason,
    });
  };

  const { mutateAsync: updateProjectCompletionNoteMutate } = useMutation({
    mutationFn: ({ note }: { note: string }) => updateProjectCompletionNoteApi(projectId, note),
    onSuccess: () => {
      // The note is rendered on both the project details page and the billing page.
      queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projectBillingDetails", projectId] });
      showSuccessMessage("Abschlussnotizen gespeichert!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 401) {
        showErrorMessage("Keine Berechtigung!");
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      } else if (error.response?.status === 422) {
        showErrorMessage("Abschlussnotizen können in diesem Status nicht mehr bearbeitet werden!");
      } else {
        showErrorMessage("Fehler beim Speichern der Abschlussnotizen!");
      }
    },
  });

  const updateProjectCompletionNote = async (note: string) => {
    await updateProjectCompletionNoteMutate({ note });
  };

  const { mutateAsync: updateProjectClosureWaiverMutate } = useMutation({
    mutationFn: ({
      waiverReason,
      plannedFollowUpActivity,
    }: {
      waiverReason: string;
      plannedFollowUpActivity?: boolean;
      showSuccessToast: boolean;
      showErrorToast: boolean;
    }) => updateProjectClosureWaiverApi(projectId, waiverReason, plannedFollowUpActivity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      queryClient.invalidateQueries({ queryKey: ["projectBillingDetails", projectId] });
    },
    onError: (error: AxiosError, variables) => {
      if (!variables.showErrorToast) {
        return;
      }
      if (error.response?.status === 401) {
        showErrorMessage("Keine Berechtigung!");
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      } else if (error.response?.status === 422) {
        showErrorMessage("Verzicht kann in diesem Status nicht mehr bearbeitet werden!");
      } else {
        showErrorMessage("Fehler beim Speichern der Verzichtsbegründung!");
      }
    },
  });

  const updateProjectClosureWaiver = async (
    waiverReason: string,
    plannedFollowUpActivity?: boolean,
    showSuccessToast = true,
    showErrorToast = true
  ) => {
    await updateProjectClosureWaiverMutate({
      waiverReason,
      plannedFollowUpActivity,
      showSuccessToast,
      showErrorToast,
    });
    if (showSuccessToast) {
      showSuccessMessage("Verzichtsbegründung gespeichert!");
    }
  };

  /**
   * Update the project data
   * @param updatedProjectData The project data to update
   */
  const updateProjectData = async (updatedProjectData: UpdatedProjectDataDto) => {
    await updateProjectDataMutate(updatedProjectData);
  };

  // updateProjectBillingDetails mutation
  const { mutateAsync: updateProjectBillingDetailsMutate } = useMutation({
    mutationFn: (billingDocument: ProjectBillingDocumentUpdateDto) =>
      updateProjectBillingDetails(projectId, billingDocument),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectBillingDetails", projectId] });
    },
    onError: () => {
      showErrorMessage("Fehler beim Speichern der Änderung!");
    },
  });

  /**
   * Complete a project billing document
   * @param documentType The type of the document to complete
   */
  const changeProjectBillingDocumentStatus = async (
    documentType:
      | "APatEV"
      | "APHold"
      | "evaluationAtEV"
      | "DLatEV"
      | "offerInAlfresco"
      | "deviationFromStandard"
      | "consultingContractProvided"
      | "teamContractProvided"
      | "qmApproval"
      | "paymentDelay"
      | "moneyReceived",
    completed: boolean
  ) => {
    await updateProjectBillingDetailsMutate({ documentType: documentType, completed: completed });
  };

  // updateProjectMemberBillingDetails mutation
  const { mutateAsync: updateProjectMemberBillingDetailsMutate } = useMutation({
    mutationFn: ({ memberId, billingDocument }: { memberId: number; billingDocument: ProjectMemberUpdateDto }) =>
      updateProjectMemberBillingDetails(projectId, memberId, billingDocument),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectBillingDetails", projectId] });
    },
    onError: () => {
      showErrorMessage("Fehler beim Speichern der Änderung!");
    },
  });

  /**
   * Update the project member billing details
   * @param memberId The ID of the member
   * @param billingDocument The billing document to update
   */
  const changeProjectMemberBillingDetails = async (memberId: number, billingDocument: ProjectMemberUpdateDto) => {
    await updateProjectMemberBillingDetailsMutate({ memberId, billingDocument });
  };

  // updateBTAllocation mutation
  const { mutateAsync: updateBTAllocationMutate } = useMutation({
    mutationFn: (memberBTAllocation: MemberBTAllocationDto[]) => updateBTAllocation(projectId, memberBTAllocation),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectBillingDetails", projectId] });
      showSuccessMessage("BT-Aufteilung erfolgreich gespeichert!");
    },
    onError: () => {
      showErrorMessage("Fehler beim Speichern der BT-Aufteilung!");
    },
  });

  /**
   * Update the BT allocation for the project
   * @param memberBTAllocation The BT allocation to update
   */
  const saveBTAllocation = async (memberBTAllocation: MemberBTAllocationDto[]) => {
    await updateBTAllocationMutate(memberBTAllocation);
  };

  // updateStaffingCommittee mutation

  const { mutateAsync: updateStaffingCommitteeMutate } = useMutation({
    mutationFn: (staffingCommittee: MembersFieldDto[]) => updateStaffingCommittee(projectId, staffingCommittee),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Besetzungsgremium erfolgreich gespeichert!");
    },
    onError: () => {
      showErrorMessage("Fehler beim Speichern des Besetzungsgremiums!");
    },
  });

  /**
   * Update the staffing committee for the project
   * @param staffingCommittee The staffing committee to update
   */
  const saveStaffingCommittee = async (staffingCommittee: MembersFieldDto[]) => {
    await updateStaffingCommitteeMutate(staffingCommittee);
  };

  // saveTeamComposition mutation
  const { mutateAsync: saveTeamCompositionMutate } = useMutation({
    mutationFn: (teamComposition: ProjectTeamCompositionDto) => saveTeamCompositionApi(projectId, teamComposition),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Team-Zusammensetzung erfolgreich gespeichert!");
      navigate(`/projekte/${projectId}`);
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 422) {
        showErrorMessage("Ungültige Team-Zusammensetzung!");
      } else {
        showErrorMessage("Fehler beim Speichern der Team-Zusammensetzung!");
      }
    },
  });

  /**
   * Save the team composition for the project
   * @param teamComposition The team composition to save
   */
  const saveTeamComposition = async (teamComposition: ProjectTeamCompositionDto) => {
    await saveTeamCompositionMutate(teamComposition);
  };

  // startSecondApplicationPhase mutation
  const { mutateAsync: startSecondApplicationPhaseMutate } = useMutation({
    mutationFn: (secondApplicationPhase: SecondApplicationPhaseDto) =>
      startSecondApplicationPhaseApi(projectId, secondApplicationPhase),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Zweite Bewerbungsphase gestartet!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 401) {
        showErrorMessage("Keine Berechtigung!");
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      } else {
        showErrorMessage("Fehler beim Starten der zweiten Bewerbungsphase!");
      }
    },
  });

  /**
   * Start the second application phase for the project
   * @param secondApplicationPhase The second application phase to start
   */
  const startSecondApplicationPhase = async (secondApplicationPhase: SecondApplicationPhaseDto) => {
    await startSecondApplicationPhaseMutate(secondApplicationPhase);
  };

  // ############
  // DELETE QUERIES
  // ############

  // deleteApplication mutation
  const { mutateAsync: deleteApplicationMutate } = useMutation({
    mutationFn: deleteApplication,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["projectDetails", projectId] });
      showSuccessMessage("Bewerbung erfolgreich gelöscht!");
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 401) {
        showErrorMessage("Keine Berechtigung!");
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      } else {
        showErrorMessage("Fehler beim Löschen der Bewerbung!");
      }
    },
  });

  /**
   * Delete the application for the project
   */
  const withdrawApplication = async () => {
    return await deleteApplicationMutate(projectId);
  };

  return {
    projectDetails,
    userApplication,
    projectApplications,
    isProjectApplicationsLoading,
    isUserApplicationLoading,
    isProjectDetailsLoading,
    projectBillingDetails,
    isProjectBillingDetailsLoading,
    updateProject,
    callOffProject,
    startTendering,
    offerAccepted,
    offerRejected,
    pitchLost,
    startBilling,
    completeProject,
    updateProjectCompletionNote,
    updateProjectClosureWaiver,
    updateProjectData,
    withdrawApplication,
    changeProjectBillingDocumentStatus,
    changeProjectMemberBillingDetails,
    saveBTAllocation,
    saveStaffingCommittee,
    saveTeamComposition,
    startSecondApplicationPhase,
  };
};

export default useProjectsDetails;
